import React, { JSX, useState } from 'react';
import useBarcodeScanner from './useBarcodeScanner';
import BarcodeScannerEntryField from './BarcodeScannerEntryField';
import type { ScannerEntryMode } from './types/ScannerTypes';
import { maybeConvertRacerangerScan } from '../../helpers/racerangerDeviceScan';

/** Case-insensitive substring match on device ID; empty needle shows all rows. */
export function deviceIdMatchesTableFilter(deviceId: string, needle: string): boolean {
  const n = needle.trim().toLowerCase();
  if (!n) return true;
  return String(deviceId ?? '').toLowerCase().includes(n);
}

type Props = {
  value: string;
  onChange: (value: string) => void;
  /**
   * Opens the serial barcode scanner and fills the field on scan. Safe to enable on
   * multiple mounted controls at once — the shared serial port is exclusive, so only
   * the most recently focused field is "connected" and receives scans.
   */
  enableScanner?: boolean;
  disabled?: boolean;
  /** Applied to the outer wrapper (layout / spacing). */
  className?: string;
  /** Applied to the inner <input> element. */
  inputClassName?: string;
  /** Controlled mode. When omitted the field manages its own toggle state internally. */
  mode?: ScannerEntryMode;
  onModeChange?: (mode: ScannerEntryMode) => void;
  /** Hide the inline Scan/Manual toggle. */
  hideEntryModeToggle?: boolean;
  /**
   * When set (and the field is uncontrolled), the Scan/Manual choice is persisted
   * to localStorage under this key and restored on mount.
   */
  persistModeKey?: string;
};

export default function DeviceIdTableFilterBar(props: Props): JSX.Element {
  const {
    value,
    onChange,
    enableScanner = false,
    disabled = false,
    className,
    inputClassName,
    mode: controlledMode,
    onModeChange,
    hideEntryModeToggle = false,
    persistModeKey,
  } = props;

  const [internalMode, setInternalMode] = useState<ScannerEntryMode>(() => {
    if (persistModeKey && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(persistModeKey);
        if (stored === 'scan' || stored === 'manual') return stored;
      } catch {
        // ignore storage failures
      }
    }
    return 'scan';
  });
  const mode = controlledMode ?? internalMode;
  const setMode = (next: ScannerEntryMode): void => {
    if (onModeChange) onModeChange(next);
    if (controlledMode === undefined) {
      setInternalMode(next);
      if (persistModeKey) {
        try {
          localStorage.setItem(persistModeKey, next);
        } catch {
          // ignore storage failures
        }
      }
    }
  };

  const { connectionActive, claim } = useBarcodeScanner({
    enabled: enableScanner && mode === 'scan',
    pollIntervalMs: 1500,
    mapScan: (raw) => maybeConvertRacerangerScan(raw),
    onScan: (v) => onChange(v),
  });

  const showToggle = enableScanner && !hideEntryModeToggle;

  return (
    <BarcodeScannerEntryField
      value={value}
      connected={connectionActive}
      disabled={disabled}
      mode={showToggle ? mode : undefined}
      onModeChange={showToggle ? setMode : undefined}
      onChange={onChange}
      onSubmit={onChange}
      onFocus={claim}
      parseValue={maybeConvertRacerangerScan}
      placeholderScan="Scan device ID…"
      placeholderManual="Filter by device ID…"
      placeholderDisconnected="Scan device ID…"
      className={inputClassName}
      wrapperClassName={className}
    />
  );
}
